import React from 'react';
import { Bed, MapMarker } from '@withjoy/joykit/icons';
import { pxToRem } from '@withjoy/joykit/theme';
import type { ControllerStateAndHelpers } from 'downshift';
import { Item } from '@withjoy/joykit/components/SelectV1/styles';
import type { OptionsType, OptionType } from '@withjoy/joykit';
import { Box, Flex, SelectV1, styled, TextV2 } from '@withjoy/joykit';
import { SELECT_SELECTED_OPTION_CLASS } from '@withjoy/joykit/components/SelectV1/constants';
import { createLocationInputV2StateReducerHandler } from './createLocationInputV2StateReducerHandler';

const Menu = styled.ul<{ maxHeight: string; divideAt?: number }>`
  min-width: ${pxToRem(176)};
  overflow: auto;
  max-height: ${props => props.maxHeight};
  ${props =>
    props.divideAt
      ? `> li:nth-child(${props.divideAt}) {
            border-bottom: 1px solid #EBEBEB;
          }`
      : undefined};
`;

const RenderItem = styled(Item)`
  border-radius: 0px;
  padding: ${props => props.theme.space[4]};
  border-bottom: 1px solid ${props => props.theme.colors.mono3};
`;

type LocationInputV2SelectV1Props<T extends OptionType> = {
  isHideListItemIcon?: boolean;
} & SelectV1<T>['props'];

type Props<T extends OptionType> = LocationInputV2SelectV1Props<T> & {
  options: T[];
  value?: T | null;
  noOptionsMessage?: string;
  loading?: boolean;
  loadingMessage?: (inputValue: string | null) => React.ReactNode;
  maxDropdownHeight?: string;
  afterMenuContent?: React.ReactNode;
  noBorder?: boolean;
  disabled?: boolean;
};

export class LocationInputV2SelectV1<T extends OptionType & { isHotel?: boolean }> extends SelectV1<T> {
  static defaultProps = {
    ...SelectV1.defaultProps,
    isHideListItemIcon: false,
  };

  props!: Props<T>;

  // Prevent Downshift v3 blur from clearing typed input when selectedItem is null in uncontrolled mode.
  protected stateReducerHandler = createLocationInputV2StateReducerHandler<T>(() => this.props);

  // Override the renderOptions method to add custom rendering logic
  protected renderOptions = (renderProps: ControllerStateAndHelpers<T>) => {
    const { selectedItem, highlightedIndex, getItemProps, inputValue } = renderProps;
    const { noOptionsMessage, loading, loadingMessage } = this.props;

    if (loading) {
      return <div>{typeof loadingMessage === 'function' ? loadingMessage(inputValue) : loadingMessage}</div>;
    }

    const filteredOptions = this.getOptions(renderProps);
    if (filteredOptions.length) {
      return (filteredOptions as OptionsType<T>).map((option, index) => {
        const isSelected = selectedItem === option;
        const isDisabled = this.isOptionDisabled(option);
        return (
          <RenderItem
            key={option.key || option.value}
            isHighlighted={highlightedIndex === index}
            isSelected={isSelected}
            isDisabled={isDisabled}
            {...getItemProps({
              key: option.key || option.value,
              item: option,
              index,
              ['aria-selected']: isSelected,
              disabled: isDisabled,
            })}
            className={isSelected && SELECT_SELECTED_OPTION_CLASS}
          >
            <Flex
              width="100%"
              columnGap={4}
              alignItems="center"
            >
              {!this.props.isHideListItemIcon && (
                <Flex
                  height={pxToRem(32)}
                  alignItems="center"
                  flexShrink={0}
                >
                  {React.isValidElement(option.iconStart) ? option.iconStart : option.isHotel ? <Bed size={24} /> : <MapMarker size={24} />}
                </Flex>
              )}
              <Flex flexDirection="column">
                <TextV2
                  typographyVariant="button2"
                  color="mono14"
                >
                  {option.label}
                </TextV2>
                <TextV2
                  typographyVariant="label2"
                  color="mono12"
                >
                  {option.subtitle}
                </TextV2>
              </Flex>
            </Flex>
          </RenderItem>
        );
      });
    }

    if (!inputValue?.trim()) return <></>;

    return (
      <Flex
        as="li"
        listStyle="none"
        minHeight={pxToRem(48)}
        justifyContent="center"
        padding={4}
      >
        {noOptionsMessage}
      </Flex>
    );
  };

  // Override the renderMenu method to use the new renderOptions method
  protected renderMenu = (renderProps: ControllerStateAndHelpers<T>) => {
    const { maxDropdownHeight, afterMenuContent = undefined } = this.props;
    const { innerRef, ...menuProps } = renderProps.getMenuProps({ refKey: 'innerRef' }, { suppressRefError: true });
    return (
      <Box
        ref={this.handleRefs.menuContainerRef}
        position="relative"
        borderRadius={pxToRem(12)}
      >
        <Menu
          {...menuProps}
          ref={innerRef}
          style={{ maxHeight: maxDropdownHeight }}
        >
          {this.renderOptions(renderProps)}
        </Menu>
        {afterMenuContent}
      </Box>
    );
  };
}
